import { useEffect, useRef, useState } from 'react';

export const MERGE_TAGS: { tag: string; desc: string; example: string }[] = [
  { tag: 'customer.first_name',           desc: 'Customer first name',           example: 'Armando' },
  { tag: 'customer.last_name',            desc: 'Customer last name',            example: 'Jimenez' },
  { tag: 'customer.phone',                desc: 'Customer phone',                example: '(469) 316-0530' },
  { tag: 'customer.email',                desc: 'Customer email',                example: 'armando.j@gmail.com' },
  { tag: 'customer.address',              desc: 'Property address',              example: '4218 Oak Ridge Dr' },
  { tag: 'customer.last_serviced_months', desc: 'Months since last service',     example: '14' },
  { tag: 'customer.sms_opt_in',           desc: 'SMS opt-in status',             example: 'true' },
  { tag: 'invoice.number',                desc: 'Invoice number',                example: '2412' },
  { tag: 'invoice.balance',               desc: 'Outstanding balance',           example: '$1,200' },
  { tag: 'invoice.balance_due',           desc: 'Balance due (numeric)',         example: '1200' },
  { tag: 'invoice.age_days',              desc: 'Days since invoice sent',       example: '7' },
  { tag: 'storm.cell_size',               desc: 'Hail cell size (inches)',       example: '2.18' },
  { tag: 'storm.cell_size_in',            desc: 'Hail cell size numeric',        example: '2.18' },
  { tag: 'storm.zip',                     desc: 'Storm zip code',                example: '75218' },
  { tag: 'schedule_link',                 desc: 'Booking page URL',              example: 'app.impactiq.us/book/abc123' },
  { tag: 'stripe_link',                   desc: 'Stripe payment link',           example: 'pay.impactiq.us/inv-2412' },
  { tag: 'review_link',                   desc: 'Google review request URL',     example: 'g.page/r/xxx' },
  { tag: 'ai_summary',                    desc: 'Output of upstream AI step',    example: 'Customer needs final balance reminder…' },
];

interface Props {
  value: string;
  onChange: (val: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}

export function MergeTagInput({ value, onChange, rows = 4, placeholder, className }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filterStart, setFilterStart] = useState<number>(-1);
  const [hoverIdx, setHoverIdx] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const filtered = open
    ? MERGE_TAGS.filter(t => t.tag.toLowerCase().includes(query.toLowerCase()))
    : [];

  function updateCaretMenu(text: string, caret: number) {
    // Look backwards from caret for "{{"
    const before = text.slice(0, caret);
    const m = before.match(/\{\{([\w.]*)$/);
    if (m && m.index !== undefined) {
      setFilterStart(m.index);
      setQuery(m[1]);
      setOpen(true);
      setHoverIdx(0);
      // Compute caret position for popup placement
      const ta = taRef.current;
      if (ta) {
        const rect = ta.getBoundingClientRect();
        // approximate caret pos with a mirror element
        const mirror = getMirror(ta);
        const before = text.slice(0, m.index);
        mirror.textContent = before;
        const span = document.createElement('span');
        span.textContent = '{{';
        mirror.appendChild(span);
        const sRect = span.getBoundingClientRect();
        const mRect = mirror.getBoundingClientRect();
        setPos({
          top: rect.top + (sRect.bottom - mRect.top) + 4,
          left: rect.left + (sRect.left - mRect.left),
        });
      }
    } else {
      setOpen(false);
      setFilterStart(-1);
      setQuery('');
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(e.target.value);
    updateCaretMenu(e.target.value, e.target.selectionStart);
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
      const ta = e.currentTarget;
      updateCaretMenu(ta.value, ta.selectionStart);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtered[hoverIdx]) {
        e.preventDefault();
        insert(filtered[hoverIdx].tag);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function insert(tag: string) {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const before = value.slice(0, filterStart);
    const after = value.slice(caret);
    const replacement = `{{${tag}}}`;
    const next = before + replacement + after;
    onChange(next);
    setOpen(false);
    // Restore caret to right after inserted tag
    setTimeout(() => {
      if (taRef.current) {
        const c = before.length + replacement.length;
        taRef.current.focus();
        taRef.current.setSelectionRange(c, c);
      }
    }, 0);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!t.closest('.mta-menu') && !t.closest('textarea')) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <>
      <textarea
        ref={taRef}
        value={value}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        rows={rows}
        placeholder={placeholder}
        className={className}
      />
      {open && filtered.length > 0 && (
        <div className="mta-menu" style={{ top: pos.top, left: pos.left }}>
          <div className="mta-head">Merge tag · {filtered.length}</div>
          {filtered.slice(0, 8).map((t, i) => (
            <div
              key={t.tag}
              className={`mta-item ${i === hoverIdx ? 'on' : ''}`}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseDown={e => { e.preventDefault(); insert(t.tag); }}
            >
              <div>
                <b>{`{{${t.tag}}}`}</b>
                <span>{t.desc}</span>
              </div>
              <code>{t.example}</code>
            </div>
          ))}
          <div className="mta-foot"><kbd>↑↓</kbd> nav · <kbd>↵</kbd> insert · <kbd>Esc</kbd> close</div>
        </div>
      )}
    </>
  );
}

let mirrorEl: HTMLDivElement | null = null;
function getMirror(ta: HTMLTextAreaElement): HTMLDivElement {
  if (mirrorEl) return mirrorEl;
  mirrorEl = document.createElement('div');
  const cs = getComputedStyle(ta);
  for (const prop of ['fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight', 'padding', 'border', 'boxSizing', 'whiteSpace', 'wordWrap']) {
    (mirrorEl.style as any)[prop] = (cs as any)[prop];
  }
  mirrorEl.style.position = 'absolute';
  mirrorEl.style.visibility = 'hidden';
  mirrorEl.style.whiteSpace = 'pre-wrap';
  mirrorEl.style.wordWrap = 'break-word';
  mirrorEl.style.overflow = 'hidden';
  document.body.appendChild(mirrorEl);
  return mirrorEl;
}
